import { useEffect, useState, type FormEvent } from 'react';
import { api, type CalendarAccountsResponse, type PushDefaults } from '../lib/api.js';
import { digitsOrBlank, friendlySaveError } from '../lib/ledgerCopy.js';
import { currentBrand } from '../lib/brand.js';

const EMPTY: PushDefaults = {
  basecamp: { projectId: null, todolistId: null, autoPush: false },
  emailTo: null,
};

/**
 * Human door for where notes can be sent.
 * Add another destination by rendering another card in this list.
 * Saving still uses the existing push-defaults API.
 */
export function IntegrationsPage(): JSX.Element {
  const [projectId, setProjectId] = useState('');
  const [todolistId, setTodolistId] = useState('');
  const [emailTo, setEmailTo] = useState('');
  const [autoPush, setAutoPush] = useState(false);
  const [accounts, setAccounts] = useState<CalendarAccountsResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let alive = true;
    void Promise.all([api.getPushDefaults(), api.calendarAccounts()])
      .then(([defaults, info]) => {
        if (!alive) return;
        apply(defaults);
        setAccounts(info);
      })
      .catch((err) => {
        if (!alive) return;
        setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      alive = false;
    };
  }, []);

  function apply(defaults: PushDefaults): void {
    setProjectId(defaults.basecamp.projectId ?? '');
    setTodolistId(defaults.basecamp.todolistId ?? '');
    setEmailTo(defaults.emailTo ?? '');
    setAutoPush(defaults.basecamp.autoPush);
  }

  async function onSave(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (busy) return;
    if (!digitsOrBlank(projectId) || !digitsOrBlank(todolistId)) {
      setError('Use digits for project and list numbers.');
      setSaved(false);
      return;
    }
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      const next = await api.savePushDefaults({
        projectId: projectId.trim(),
        todolistId: todolistId.trim(),
        emailTo: emailTo.trim(),
        autoPush,
      });
      apply(next ?? EMPTY);
      setSaved(true);
    } catch (err) {
      setError(friendlySaveError(err));
    } finally {
      setBusy(false);
    }
  }

  const basecamp = accounts?.basecamp ?? null;
  const basecampOn = !!accounts?.basecampConfigured;
  const showBasecampFields = !!basecamp || projectId.trim() !== '' || todolistId.trim() !== '';

  return (
    <div>
      <h1 className="page-title">Integrations</h1>
      <p className="page-subtitle">
        Choose where action items can go. Email works for everyone. Basecamp is here if your team uses it.
      </p>
      <form onSubmit={(event) => void onSave(event)}>
        <div className="integration-list">
          <section className="panel" id="email">
            <div className="integration-card__head">
              <div>
                <h3 className="panel__title">Email</h3>
                <p className="panel__subtle">Send action items by email.</p>
              </div>
              <span className="pill pill--ok">Ready</span>
            </div>
            <div className="form-row">
              <label className="form-label" htmlFor="pushEmail">Send action items to</label>
              <input
                id="pushEmail"
                type="email"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                placeholder="you@company.com"
              />
              <span className="form-help">Leave this blank to use your sign-in address.</span>
            </div>
          </section>

          <section className="panel" id="basecamp">
            <div className="integration-card__head">
              <div>
                <h3 className="panel__title">Basecamp</h3>
                <p className="panel__subtle">
                  Send action items to a Basecamp to-do list. One option among the places you connect.
                </p>
              </div>
              {accounts && (
                <span className={basecamp ? 'pill pill--ok' : 'pill'}>
                  {!basecampOn ? 'Not available' : basecamp ? 'Connected' : 'Not connected'}
                </span>
              )}
            </div>

            {accounts && !basecampOn && (
              <p className="panel__subtle">Basecamp is not available on this {currentBrand().name} yet.</p>
            )}

            {basecampOn && accounts && !accounts.encryptionReady && (
              <div className="form-error">Connecting accounts needs encryption set up on this {currentBrand().name}. Ask an admin.</div>
            )}

            {basecampOn && (
              <div className="integration-card__actions">
                <a className="btn btn--ghost" href="/api/oauth/basecamp/start">
                  {basecamp ? 'Reconnect Basecamp' : 'Connect Basecamp'}
                </a>
                {basecamp && (
                  <span className="panel__subtle">
                    Connected{basecamp.name ? ` as ${basecamp.name}` : ''}{basecamp.email ? ` (${basecamp.email})` : ''}.
                  </span>
                )}
              </div>
            )}

            {basecampOn && showBasecampFields && (
              <>
                <div className="form-row">
                  <label className="form-label" htmlFor="bcProject">Project number</label>
                  <input
                    id="bcProject"
                    value={projectId}
                    onChange={(e) => setProjectId(e.target.value)}
                    placeholder="From the project web address"
                    inputMode="numeric"
                  />
                  <span className="form-help">Open the project in Basecamp. Copy the number from the web address.</span>
                </div>
                <div className="form-row">
                  <label className="form-label" htmlFor="bcList">To-do list number</label>
                  <input
                    id="bcList"
                    value={todolistId}
                    onChange={(e) => setTodolistId(e.target.value)}
                    placeholder="Optional"
                    inputMode="numeric"
                  />
                  <span className="form-help">Optional. The first list in the project is used when this is blank.</span>
                </div>
                <label className="toggle">
                  <input type="checkbox" checked={autoPush} onChange={(e) => setAutoPush(e.target.checked)} />
                  After a meeting finishes, send open action items to Basecamp
                </label>
              </>
            )}

            {basecampOn && !showBasecampFields && (
              <p className="panel__subtle" style={{ marginTop: 12 }}>
                After you connect, add the project that should receive action items.
              </p>
            )}
          </section>

          {error && <div className="form-error">{error}</div>}
          {saved && <div className="form-success">Saved.</div>}
          <div>
            <button type="submit" className="btn" disabled={busy}>
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
